import { useState } from "react";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Trash2, Loader2,
  RotateCw
} from "lucide-react";

import {
  Card, CardContent, CardFooter, CardHeader, CardTitle
} from "../ui/card.jsx";
import { Progress } from "../ui/progress.jsx";
import {
  DeleteStakingRiskProductByIDThunk, 
  GetAllDeletedStakingRiskProductsThunk
} from "../../../Store/Thunks/StakingRiskThunk.js";
import { cn } from "../../../StakingRisk/lib/utils.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "../ui/alert-dialog.jsx";
import { getProductCompletionStats, getSafetyIndexInfo } from "../../../StakingRisk/utils/product-analytics.js";
import { DELETE_TYPES } from "../../../StakingRisk/constants/index.js";

export default function TrashProductCard({ product }) {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { completionPercentage } = getProductCompletionStats(product);
  const safety_index = product.safetyIndex;

  const safetyInfo = getSafetyIndexInfo(safety_index);

  const handleDelete = async () => {
      setIsDeleting(true);
      await dispatch(DeleteStakingRiskProductByIDThunk(product.id, DELETE_TYPES.DELETE));
      await dispatch(GetAllDeletedStakingRiskProductsThunk()); //refresh list
      setIsDeleting(false);
      setIsOpen(false);
};

const handleRestore = async () => {
  setIsDeleting(true);
  await dispatch(DeleteStakingRiskProductByIDThunk(product.id, DELETE_TYPES.RESTORE));
  await dispatch(GetAllDeletedStakingRiskProductsThunk()); //refresh list
  setIsDeleting(false);
  setIsOpen(false);
};

  return (
    <div className="relative group h-full">
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
             handleRestore()          }}
          className="h-7 w-7 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-gray-500 hover:text-blue-500 rounded-full"
          title="Restore Product"
        >
          <RotateCw className="h-4 w-4" />
        </button>

       <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
  <AlertDialogTrigger asChild>
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
      }}
      className="h-7 w-7 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/40 text-gray-500 hover:text-red-500 rounded-full"
      title="Delete product"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  </AlertDialogTrigger>

  <AlertDialogContent>
    <AlertDialogHeader>
      <AlertDialogTitle>Permanently Delete?</AlertDialogTitle>
      <AlertDialogDescription>
        This will permanently delete the staking product
        <span className="font-semibold"> "{product.name}"</span>.  
        This action <span className="font-semibold text-red-600">cannot be undone</span>.
      </AlertDialogDescription>
    </AlertDialogHeader>

    <AlertDialogFooter>
      <AlertDialogCancel onClick={() => setIsOpen(false)} disabled={isDeleting}>
        Cancel
      </AlertDialogCancel>
      <AlertDialogAction
        onClick={handleDelete}
        disabled={isDeleting}
        className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
      >
        {isDeleting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Deleting...
          </>
        ) : (
          "Delete Permanently"
        )}
      </AlertDialogAction>
    </AlertDialogFooter>
  </AlertDialogContent>
</AlertDialog>

      </div>

      <Link to={`/stakingrisk/product-detail/${product.id}`} className="block h-full">
        <Card className={`hover:shadow-xl transition-all h-full flex flex-col border-b border-l border-r dark:border-gray-800 border-t-4 ${safetyInfo.borderColor}`}>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start mb-1">
              <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                {product.name}
              </CardTitle>
               <div className={cn("px-2 py-0.5 text-xs font-semibold rounded-full inline-block", safetyInfo.bgColor, safetyInfo.color)}>
                 {safetyInfo.category}
            </div>
            </div>
          </CardHeader>

          <CardContent className="flex-grow flex flex-col justify-center items-center text-center p-4">
            <div className="text-4xl font-bold tracking-tight">            
              {`${Math.round(safety_index)}%`}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Safety Index</p>
            <div className="w-full px-4">
              <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span>Assessment Progress</span>
                <span>{Math.round(completionPercentage)}%</span>
              </div>
              <Progress value={completionPercentage} className="h-1.5" />
            </div>
          </CardContent>

          <CardFooter className="flex justify-between items-center text-xs text-gray-400 dark:text-gray-500 p-4 pt-2">
            <span>Deleted: {format(new Date(product.createdAt), "dd-MMM-yyyy")}</span>
          </CardFooter>
        </Card>
      </Link>
    </div>
  );
}
